import { memo, forwardRef, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { components } from "react-select";
import "./dropdown.scss";
import CustomTooltip from "../Tooltip/CustomTooltip";
import { slice } from "../../../Utils/stringUtils";

const Dropdown = memo(
  forwardRef((props, ref) => {
    const {
      options,
      isMulti,
      isDisabled,
      isClearable,
      isLoading,
      isSearchable,
      name,
      isRtl,
      value,
      onChange,
      label,
      placeholder,
      isRequired,
      additonalSearch,
      onKeyDown,
      onNoOptionsClick,
      isAddNew,
      ...restProps
    } = props;
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
      if (name) {
        if (isMulti) {
          // For multi-select, name should be an array of values
          const selectedOptions = options.filter((option) => name.includes(option.value));
          setSelectedOption(selectedOptions);
        } else {
          const foundOption = options.find((option) => option.value === name); // eslint-disable-line eqeqeq
          if (foundOption) {
            setSelectedOption(foundOption);
          }
        }
      }
    }, [name, options, isMulti]);

    const handleChange = (selectedOption) => {
      if (onChange) {
        if (isMulti) {
          // For multi-select, return array of values
          onChange(selectedOption ? selectedOption.map((option) => option.value) : []);
        } else {
          onChange(selectedOption ? selectedOption.value : "");
        }
      }
    };

    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        boxShadow: state.isFocused ? null : provided.boxShadow,
        borderColor: state.isFocused ? "var(--border-light)" : provided.borderColor,
        "&:hover": {
          borderColor: state.isFocused ? "var(--secondary)" : provided.borderColor,
        },
      }),
    };
    const CustomInput = (props) => {
      const { onKeyDown } = props.selectProps;
      return (
        <components.Input
          {...props}
          onKeyDown={(e) => {
            if (onKeyDown) onKeyDown(e);
            props.onKeyDown?.(e); // Preserve default behavior
          }}
        />
      );
    };
    const CustomOption = (props) => {
      return (
        <components.Option {...props}>
          <div className="d-flex flex-column">
            {/* <strong style={{ textTransform: "uppercase" }}>{slice(props?.data?.label || '', 20)}</strong> */}
            <strong style={{ textTransform: "uppercase" }}>{props?.data?.label}</strong>

            <CustomTooltip text={props?.data?.description || ""}>
              <small>{slice(props?.data?.description || "", 30)}</small>
            </CustomTooltip>
          </div>
        </components.Option>
      );
    };
    const customFilterOption = (option, inputValue) => {
      const input = inputValue?.toLowerCase();
      const label = option?.label?.toLowerCase();
      const description = option?.data?.description?.toLowerCase(); // Fixed typo

      return label?.includes(input) || description?.includes(input);
    };
    const CustomNoOptionsMessage = (props) => {
      const inputValue = props.selectProps.inputValue || "";

      // Only show "No options" message if input has at least 3 characters
      if (inputValue.length < 3) {
        return isAddNew ? (
          <components.NoOptionsMessage {...props}>
            <div style={{ padding: "4px 8px" }}>Plese enter 3 character to add new medicine</div>
          </components.NoOptionsMessage>
        ) : (
          <components.NoOptionsMessage {...props}>
            <div style={{ padding: "4px 8px" }}>No Option found</div>
          </components.NoOptionsMessage>
        );
      }

      return isAddNew ? (
        <components.NoOptionsMessage {...props}>
          <div
            className="no-options-message"
            onClick={() => {
              onNoOptionsClick?.(inputValue);
              props.selectProps.onMenuClose();
            }}
            style={{
              cursor: "pointer",
              color: "var(--primary)",
              padding: "4px 8px",
            }}
          >
            No options found. Click to add medicine.
          </div>
        </components.NoOptionsMessage>
      ) : (
        <components.NoOptionsMessage {...props}>
          <div style={{ padding: "4px 8px" }}>No options found.</div>
        </components.NoOptionsMessage>
      );
    };

    return (
      <>
        <Form.Label className={isRequired ? "required" : ""} aria-label={label}>
          {label}
        </Form.Label>

        <Select
          ref={ref}
          className="basic-single mb-3"
          classNamePrefix="select"
          defaultValue={selectedOption}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isRtl={isRtl}
          isSearchable={isSearchable}
          onKeyDown={onKeyDown}
          name={name}
          menuPosition="fixed" // Use fixed for dropdown visibility in modals/scrollable containers
          menuPlacement="bottom"
          menuPortalTarget={document.body} // Portal it to the body to fix positioning
          styles={{
            ...customStyles,
            menu: (provided) => ({
              ...provided,
              zIndex: 9999, // ensure it's above all UI
              minWidth: "100%",
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }), // also for portal
          }}
          components={{
            Option: CustomOption,
            Input: CustomInput,
            NoOptionsMessage: CustomNoOptionsMessage,
          }}
          options={options}
          getOptionLabel={({ label }) => label}
          getOptionValue={({ value }) => value}
          value={
            isMulti
              ? options?.filter(({ value }) => name?.includes(value))
              : options?.filter(({ value }) => value === name)
          }
          onChange={handleChange}
          placeholder={`Select ${placeholder || "Value"}`}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
            border: 0,
            colors: {
              ...theme.colors,
              primary25: "var(--border-light)",
              primary: "var(--primary)",
            },
          })}
          filterOption={additonalSearch ? customFilterOption : undefined}
          isMulti={isMulti}
        />
      </>
    );
  }),
);

export default Dropdown;
